import type {
  Database,
  Company,
  User,
  Project,
  ContractItem,
  InterimApplication,
  Tender,
  RetentionLedger,
  Task,
  TaskComment,
  Notification,
  ShareToken,
  RateLibrary,
  MainContractor,
  Contact,
  EstimatingTeamMember,
  Supplier,
  TermsTemplate,
  RateLibraryItem,
  RICSBenchmark,
  CommercialHealthMetric,
  Employer,
  ClientEmployerRelationship,
  EntitlementAlert,
  ActiveDispute,
  ClientRequest,
  PendingDocumentUpload,
} from '../../../types/database';
import type { Claim, ClaimCorrespondence, ClaimSummaryMetrics, ClaimFilters } from '../../../types/claims';
import type { Document } from '../../../types/documents';
import type {
  Activity,
  ActivityFilter,
  ActivityStats,
  CreatePhoneCallData,
  CreateMeetingData,
  CreateEmailData,
  CreateNoteData,
  CreateCommentData,
} from '../../../types/activity';
import { ProjectFoldersService } from '../../ProjectFoldersService';
import { projectId, publicAnonKey } from '../../../utils/supabase/info';
import { geocodeAddress } from '../../../utils/geocoding';
import type { Constructor } from './mixins';

export const withFinance = <TBase extends Constructor<any>>(Base: TBase) => class Finance extends Base {
  // RATE LIBRARIES
  // ============================================================================

  public getRateLibraries(): RateLibrary[] {
    return Object.values(this.db.rateLibraries);
  }

  public getRateLibrary(id: string): RateLibrary | undefined {
    return this.db.rateLibraries[id];
  }

  public getRateLibrariesByCompany(companyId: string): RateLibrary[] {
    return this.getRateLibraries().filter(lib => lib.companyId === companyId || lib.isPublic);
  }

  public createRateLibrary(library: Omit<RateLibrary, 'id'>): RateLibrary {
    const newLibrary: RateLibrary = {
      ...library,
      id: this.generateId(),
    };
    this.db.rateLibraries[newLibrary.id] = newLibrary;
    this.saveToStorage();
    this.notifyListeners();
    return newLibrary;
  }

  public updateRateLibrary(id: string, updates: Partial<RateLibrary>): RateLibrary | undefined {
    const library = this.db.rateLibraries[id];
    if (!library) return undefined;
    
    const updated = {
      ...library,
      ...updates,
      id,
      lastUpdated: new Date().toISOString(),
    };
    this.db.rateLibraries[id] = updated;
    this.saveToStorage();
    this.notifyListeners();
    return updated;
  }

  public deleteRateLibrary(id: string): boolean {
    if (!this.db.rateLibraries[id]) return false;
    delete this.db.rateLibraries[id];
    this.saveToStorage();
    this.notifyListeners();
    return true;
  }

  // ============================================================================

  // UPCOMING ITEMS
  // ============================================================================

  public getUpcomingItems(): any[] {
    return Object.values(this.db.upcomingItems || {});
  }

  public getUpcomingItem(id: string): any | undefined {
    return this.db.upcomingItems?.[id];
  }

  public getUpcomingItemsByProject(projectId: string): any[] {
    return this.getUpcomingItems().filter(item => item.projectId === projectId);
  }

  public getUpcomingItemsByStatus(status: string): any[] {
    return this.getUpcomingItems().filter(item => item.status === status);
  }

  public getUpcomingItemsByType(type: string): any[] {
    return this.getUpcomingItems().filter(item => item.type === type);
  }

  public createUpcomingItem(item: any): any {
    const newItem = {
      ...item,
      id: this.generateId(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    if (!this.db.upcomingItems) {
      this.db.upcomingItems = {};
    }
    this.db.upcomingItems[newItem.id] = newItem;
    this.saveToStorage();
    this.notifyListeners();
    return newItem;
  }

  public updateUpcomingItem(id: string, updates: any): any | undefined {
    if (!this.db.upcomingItems?.[id]) return undefined;
    
    const updated = {
      ...this.db.upcomingItems[id],
      ...updates,
      id,
      updatedAt: new Date().toISOString(),
    };
    this.db.upcomingItems[id] = updated;
    this.saveToStorage();
    this.notifyListeners();
    return updated;
  }

  public deleteUpcomingItem(id: string): boolean {
    if (!this.db.upcomingItems?.[id]) return false;
    delete this.db.upcomingItems[id];
    this.saveToStorage();
    this.notifyListeners();
    return true;
  }

  // ============================================================================

  // RISKS
  // ============================================================================

  public getRisks(): any[] {
    return Object.values(this.db.risks || {});
  }

  public getRisk(id: string): any | undefined {
    return this.db.risks?.[id];
  }

  public getRisksByProject(projectId: string): any[] {
    return this.getRisks().filter(risk => risk.projectId === projectId);
  }

  public getRisksBySeverity(severity: string): any[] {
    return this.getRisks().filter(risk => risk.severity === severity);
  }

  public getRisksByStatus(status: string): any[] {
    return this.getRisks().filter(risk => risk.status === status);
  }

  public createRisk(risk: any): any {
    const newRisk = {
      ...risk,
      id: this.generateId(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    if (!this.db.risks) {
      this.db.risks = {};
    }
    this.db.risks[newRisk.id] = newRisk;
    this.saveToStorage();
    this.notifyListeners();
    return newRisk;
  }

  public updateRisk(id: string, updates: any): any | undefined {
    if (!this.db.risks?.[id]) return undefined;
    
    const updated = {
      ...this.db.risks[id],
      ...updates,
      id,
      updatedAt: new Date().toISOString(),
    };
    this.db.risks[id] = updated;
    this.saveToStorage();
    this.notifyListeners();
    return updated;
  }

  public deleteRisk(id: string): boolean {
    if (!this.db.risks?.[id]) return false;
    delete this.db.risks[id];
    this.saveToStorage();
    this.notifyListeners();
    return true;
  }

  // ============================================================================

  // PIPELINE METRICS (Calculated on-the-fly from tenders and estimates)
  // ============================================================================

  public getPipelineMetrics() {
    const tenders = this.getTenders() as Tender[];
    const activeTenders = tenders.filter((t: any) => t.status === 'active' || t.status === 'pricing');

    return {
      total_inquiries: tenders.length,
      active_estimates: activeTenders.filter((t: any) => t.stage === 'pricing' || t.stage === 'pricing_in_progress').length,
      quotes_pending: activeTenders.filter((t: any) => t.stage === 'quote_pending_review' || t.stage === 'pricing').length,
      pipeline_value: activeTenders.reduce((sum: number, t: any) => sum + (t.ourFeeQuoted || 0), 0),
      win_rate: 72, // TODO: Calculate from historical data
      average_quote_value: activeTenders.length > 0
        ? activeTenders.reduce((sum: number, t: any) => sum + (t.ourFeeQuoted || 0), 0) / activeTenders.length
        : 0,
      deadlines_this_week: activeTenders.filter((t: any) => {
        if (!t.tenderDeadline) return false;
        const deadline = new Date(t.tenderDeadline);
        const now = new Date();
        const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        return deadline >= now && deadline <= weekFromNow;
      }).length,
      quotes_due_today: activeTenders.filter((t: any) => {
        if (!t.tenderDeadline) return false;
        const deadline = new Date(t.tenderDeadline);
        const today = new Date();
        return deadline.toDateString() === today.toDateString();
      }).length,
    };
  }

  // ============================================================================

  // ESTIMATING TEAM
  // ============================================================================

  public getEstimatingTeam(): EstimatingTeamMember[] {
    return Object.values(this.db.estimatingTeam || {});
  }

  public getEstimatingTeamMember(id: string): EstimatingTeamMember | undefined {
    return this.db.estimatingTeam?.[id];
  }

  public createEstimatingTeamMember(member: Omit<EstimatingTeamMember, 'id' | 'createdAt' | 'updatedAt'>): EstimatingTeamMember {
    const id = this.generateId();
    const now = new Date().toISOString();
    const newMember: EstimatingTeamMember = {
      ...member,
      id,
      createdAt: now,
      updatedAt: now,
    };
    this.db.estimatingTeam = this.db.estimatingTeam || {};
    this.db.estimatingTeam[id] = newMember;
    this.saveToStorage();
    this.notifyListeners();
    return newMember;
  }

  public updateEstimatingTeamMember(id: string, updates: Partial<EstimatingTeamMember>): EstimatingTeamMember | null {
    const member = this.db.estimatingTeam?.[id];
    if (!member) return null;

    const updated: EstimatingTeamMember = {
      ...member,
      ...updates,
      id,
      updatedAt: new Date().toISOString(),
    };
    this.db.estimatingTeam[id] = updated;
    this.saveToStorage();
    this.notifyListeners();
    return updated;
  }

  public deleteEstimatingTeamMember(id: string): boolean {
    if (!this.db.estimatingTeam?.[id]) return false;
    delete this.db.estimatingTeam[id];
    this.saveToStorage();
    this.notifyListeners();
    return true;
  }

  // ============================================================================

  // SUPPLIERS
  // ============================================================================

  public getSuppliers(): Supplier[] {
    return Object.values(this.db.suppliers || {});
  }

  public getSupplier(id: string): Supplier | undefined {
    return this.db.suppliers?.[id];
  }

  public getPreferredSuppliers(): Supplier[] {
    return this.getSuppliers().filter(s => s.preferredSupplier && s.status === 'active');
  }

  public getSuppliersByCategory(category: string): Supplier[] {
    return this.getSuppliers().filter(s => s.category === category && s.status === 'active');
  }

  public createSupplier(supplier: Omit<Supplier, 'id' | 'createdAt' | 'updatedAt'>): Supplier {
    const id = this.generateId();
    const now = new Date().toISOString();
    const newSupplier: Supplier = {
      ...supplier,
      id,
      createdAt: now,
      updatedAt: now,
    };
    this.db.suppliers = this.db.suppliers || {};
    this.db.suppliers[id] = newSupplier;
    this.saveToStorage();
    this.notifyListeners();
    return newSupplier;
  }

  public updateSupplier(id: string, updates: Partial<Supplier>): Supplier | null {
    const supplier = this.db.suppliers?.[id];
    if (!supplier) return null;

    const updated: Supplier = {
      ...supplier,
      ...updates,
      id,
      updatedAt: new Date().toISOString(),
    };
    this.db.suppliers[id] = updated;
    this.saveToStorage();
    this.notifyListeners();
    return updated;
  }

  public deleteSupplier(id: string): boolean {
    if (!this.db.suppliers?.[id]) return false;
    delete this.db.suppliers[id];
    this.saveToStorage();
    this.notifyListeners();
    return true;
  }

  // ============================================================================

  // TERMS TEMPLATES
  // ============================================================================

  public getTermsTemplates(): TermsTemplate[] {
    return Object.values(this.db.termsTemplates || {});
  }

  public getTermsTemplate(id: string): TermsTemplate | undefined {
    return this.db.termsTemplates?.[id];
  }

  public getActiveTermsTemplates(): TermsTemplate[] {
    return this.getTermsTemplates().filter(t => t.status === 'active');
  }

  public getTermsTemplatesByCategory(category: string): TermsTemplate[] {
    return this.getTermsTemplates().filter(t => t.category === category && t.status === 'active');
  }

  public getDefaultTermsTemplates(): TermsTemplate[] {
    return this.getTermsTemplates().filter(t => t.isDefault && t.status === 'active');
  }

  public createTermsTemplate(template: Omit<TermsTemplate, 'id' | 'createdAt' | 'updatedAt'>): TermsTemplate {
    const id = this.generateId();
    const now = new Date().toISOString();
    const newTemplate: TermsTemplate = {
      ...template,
      id,
      createdAt: now,
      updatedAt: now,
    };
    this.db.termsTemplates = this.db.termsTemplates || {};
    this.db.termsTemplates[id] = newTemplate;
    this.saveToStorage();
    this.notifyListeners();
    return newTemplate;
  }

  public updateTermsTemplate(id: string, updates: Partial<TermsTemplate>): TermsTemplate | null {
    const template = this.db.termsTemplates?.[id];
    if (!template) return null;

    const updated: TermsTemplate = {
      ...template,
      ...updates,
      id,
      updatedAt: new Date().toISOString(),
    };
    this.db.termsTemplates[id] = updated;
    this.saveToStorage();
    this.notifyListeners();
    return updated;
  }

  public deleteTermsTemplate(id: string): boolean {
    if (!this.db.termsTemplates?.[id]) return false;
    delete this.db.termsTemplates[id];
    this.saveToStorage();
    this.notifyListeners();
    return true;
  }

  // ============================================================================

  // RATE LIBRARY ITEMS
  // ============================================================================

  public getRateLibraryItems(): RateLibraryItem[] {
    return Object.values(this.db.rateLibraryItems || {});
  }

  public getRateLibraryItem(id: string): RateLibraryItem | undefined {
    return this.db.rateLibraryItems?.[id];
  }

  public getRateLibraryItemsByLibrary(libraryId: string): RateLibraryItem[] {
    return this.getRateLibraryItems().filter(item => item.libraryId === libraryId);
  }

  public searchRateLibraryItems(searchTerm: string): RateLibraryItem[] {
    const term = searchTerm.toLowerCase();
    return this.getRateLibraryItems().filter(item =>
      item.description?.toLowerCase().includes(term) ||
      item.code.toLowerCase().includes(term)
    );
  }

  public createRateLibraryItem(item: Omit<RateLibraryItem, 'id' | 'createdAt' | 'updatedAt'>): RateLibraryItem {
    const id = this.generateId();
    const now = new Date().toISOString();
    const newItem: RateLibraryItem = {
      ...item,
      id,
      createdAt: now,
      updatedAt: now,
    };
    this.db.rateLibraryItems = this.db.rateLibraryItems || {};
    this.db.rateLibraryItems[id] = newItem;
    this.saveToStorage();
    this.notifyListeners();
    return newItem;
  }

  public updateRateLibraryItem(id: string, updates: Partial<RateLibraryItem>): RateLibraryItem | null {
    const item = this.db.rateLibraryItems?.[id];
    if (!item) return null;

    const updated: RateLibraryItem = {
      ...item,
      ...updates,
      id,
      updatedAt: new Date().toISOString(),
    };
    this.db.rateLibraryItems[id] = updated;
    this.saveToStorage();
    this.notifyListeners();
    return updated;
  }

  public deleteRateLibraryItem(id: string): boolean {
    if (!this.db.rateLibraryItems?.[id]) return false;
    delete this.db.rateLibraryItems[id];
    this.saveToStorage();
    this.notifyListeners();
    return true;
  }

  // ============================================================================

  // RICS BENCHMARKS
  // ============================================================================

  public getRICSBenchmarks(): RICSBenchmark[] {
    return Object.values(this.db.ricsBenchmarks || {});
  }

  public getRICSBenchmark(id: string): RICSBenchmark | undefined {
    return this.db.ricsBenchmarks?.[id];
  }

  public getRICSBenchmarkByCostCode(costCode: string): RICSBenchmark | undefined {
    return this.getRICSBenchmarks().find(b => b.costCode === costCode);
  }

  public getRICSBenchmarksByCategory(category: string): RICSBenchmark[] {
    return this.getRICSBenchmarks().filter(b => b.category === category);
  }

  public getRICSBenchmarksByRegion(region: string): RICSBenchmark[] {
    return this.getRICSBenchmarks().filter(b => b.region === region);
  }

  public createRICSBenchmark(benchmark: Omit<RICSBenchmark, 'id' | 'createdAt' | 'updatedAt'>): RICSBenchmark {
    const id = this.generateId();
    const now = new Date().toISOString();
    const newBenchmark: RICSBenchmark = {
      ...benchmark,
      id,
      createdAt: now,
      updatedAt: now,
    };
    this.db.ricsBenchmarks = this.db.ricsBenchmarks || {};
    this.db.ricsBenchmarks[id] = newBenchmark;
    this.saveToStorage();
    this.notifyListeners();
    return newBenchmark;
  }

  public updateRICSBenchmark(id: string, updates: Partial<RICSBenchmark>): RICSBenchmark | null {
    const benchmark = this.db.ricsBenchmarks?.[id];
    if (!benchmark) return null;

    const updated: RICSBenchmark = {
      ...benchmark,
      ...updates,
      id,
      updatedAt: new Date().toISOString(),
    };
    this.db.ricsBenchmarks[id] = updated;
    this.saveToStorage();
    this.notifyListeners();
    return updated;
  }

  public deleteRICSBenchmark(id: string): boolean {
    if (!this.db.ricsBenchmarks?.[id]) return false;
    delete this.db.ricsBenchmarks[id];
    this.saveToStorage();
    this.notifyListeners();
    return true;
  }

  // ============================================================================

  // COMMERCIAL HEALTH METRICS
  // ============================================================================

  public getCommercialHealthMetrics(): CommercialHealthMetric[] {
    return Object.values(this.db.commercialHealthMetrics || {});
  }

  public getCommercialHealthMetric(id: string): CommercialHealthMetric | undefined {
    return this.db.commercialHealthMetrics?.[id];
  }

  public getCommercialHealthMetricByProject(projectId: string): CommercialHealthMetric | undefined {
    return this.getCommercialHealthMetrics().find(m => m.projectId === projectId);
  }

  public createCommercialHealthMetric(metric: Omit<CommercialHealthMetric, 'id' | 'createdAt' | 'updatedAt'>): CommercialHealthMetric {
    const id = this.generateId();
    const now = new Date().toISOString();
    const newMetric: CommercialHealthMetric = {
      ...metric,
      id,
      createdAt: now,
      updatedAt: now,
    };
    this.db.commercialHealthMetrics = this.db.commercialHealthMetrics || {};
    this.db.commercialHealthMetrics[id] = newMetric;
    this.saveToStorage();
    this.notifyListeners();
    return newMetric;
  }

  public updateCommercialHealthMetric(id: string, updates: Partial<CommercialHealthMetric>): CommercialHealthMetric | null {
    const metric = this.db.commercialHealthMetrics?.[id];
    if (!metric) return null;

    const updated: CommercialHealthMetric = {
      ...metric,
      ...updates,
      id,
      updatedAt: new Date().toISOString(),
    };
    this.db.commercialHealthMetrics[id] = updated;
    this.saveToStorage();
    this.notifyListeners();
    return updated;
  }

  public deleteCommercialHealthMetric(id: string): boolean {
    if (!this.db.commercialHealthMetrics?.[id]) return false;
    delete this.db.commercialHealthMetrics[id];
    this.saveToStorage();
    this.notifyListeners();
    return true;
  }

  // ============================================================================
};
